import * as React from 'react';

import { maybeParseCalendarDate } from '@rematter/common/dateTime';
import { maybe, ensureValue, Select } from '@rematter/ui-web';
import {
  Button,
  FormattedAddress,
  LocalizedDate,
  TextField,
} from '@rematter/ui-web';
import { ContentCard, ContentInfo, ContentSection } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import * as state from '../../_state/purchaseOrderDetails.state';

export const PurchaseOrderTabContract = () => {
  const { data: purchaseOrder, isSuccess } = useAtomValue(
    state.$purchaseOrderDetails,
  );
  const updatePurchaseOrder = useSetAtom(state.$updatePurchaseOrder);

  const [localPoNumber, setLocalPoNumber] = React.useState(
    purchaseOrder?.purchaseOrderNumber,
  );
  const [localContactId, setLocalContactId] = React.useState(
    purchaseOrder?.contact?.id,
  );

  const hasChanges = React.useMemo(
    () =>
      localPoNumber !== purchaseOrder?.purchaseOrderNumber ||
      localContactId !== purchaseOrder?.contact?.id,
    [
      localContactId,
      localPoNumber,
      purchaseOrder?.contact?.id,
      purchaseOrder?.purchaseOrderNumber,
    ],
  );

  const resetChanges = React.useCallback(() => {
    setLocalPoNumber(purchaseOrder?.purchaseOrderNumber);
    setLocalContactId(purchaseOrder?.contact?.id);
  }, [purchaseOrder?.contact?.id, purchaseOrder?.purchaseOrderNumber]);

  const handleSaveChanges = React.useCallback(() => {
    updatePurchaseOrder({
      id: ensureValue(purchaseOrder?.id),
      purchaseOrderNumber: maybe(localPoNumber),
      contactId: localContactId,
    });
  }, [localContactId, localPoNumber, purchaseOrder?.id, updatePurchaseOrder]);

  const handlePoNumberKeydown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        handleSaveChanges();
      }
    },
    [handleSaveChanges],
  );

  React.useEffect(() => {
    if (isSuccess) resetChanges();
  }, [isSuccess, resetChanges]);

  const contactOptions = React.useMemo(
    () =>
      purchaseOrder?.supplier?.contacts?.map(
        (contact: { id: string; fullName: string }) => ({
          key: contact.id,
          label: contact.fullName,
        }),
      ) ?? [],
    [purchaseOrder?.supplier?.contacts],
  );

  return (
    <div className={'@PurchaseOrderTabContract flex flex-col gap-5 p-5'}>
      <ContentCard noXPadding>
        <ContentSection title="Contract info" className="mx-5">
          <div className="grid grid-cols-3 gap-4">
            <ContentInfo label="Purchase Order #" value={maybe(localPoNumber)}>
              {(poNumber) => (
                <TextField
                  value={poNumber}
                  onChange={(value) => setLocalPoNumber(value)}
                  onKeyDown={handlePoNumberKeydown}
                />
              )}
            </ContentInfo>
            <ContentInfo label="Start Date" value={purchaseOrder?.startDate}>
              {(startDate) => (
                <LocalizedDate
                  date={maybeParseCalendarDate(startDate)}
                  dateStyle="medium"
                  showRelativeDate
                />
              )}
            </ContentInfo>
            <ContentInfo label="End Date" value={purchaseOrder?.endDate}>
              {(endDate) => (
                <LocalizedDate
                  date={maybeParseCalendarDate(endDate)}
                  dateStyle="medium"
                  showRelativeDate
                />
              )}
            </ContentInfo>
          </div>
        </ContentSection>
        <ContentSection title="Supplier Information" className="mx-5">
          <div className="grid grid-cols-3 gap-4">
            <ContentInfo
              label="Supplier Name"
              value={purchaseOrder?.supplier?.name}
            >
              {(supplierName) => supplierName}
            </ContentInfo>
            <ContentInfo
              label="Supplier Location"
              value={purchaseOrder?.supplier?.addresses?.at(0)}
            >
              {(address) => <FormattedAddress {...address} />}
            </ContentInfo>
            <ContentInfo label="Contact" value={true}>
              {() => (
                <Select
                  value={localContactId}
                  onChange={setLocalContactId}
                  options={contactOptions}
                />
              )}
            </ContentInfo>
          </div>
        </ContentSection>
        <ContentSection title="Payment Info" className="mx-5">
          <div className="grid grid-cols-3 gap-4">
            <ContentInfo
              label="Payment Currency"
              value={purchaseOrder?.paymentCurrency}
            >
              {(paymentMethod) => paymentMethod}
            </ContentInfo>
            <ContentInfo
              label="Payment Term"
              value={purchaseOrder?.paymentTerm}
            >
              {(paymentMethod) => paymentMethod}
            </ContentInfo>
            <ContentInfo
              label="Freight Term"
              value={purchaseOrder?.freightTerm}
            >
              {(freightTerm) => freightTerm}
            </ContentInfo>
          </div>
        </ContentSection>
        <div className="flex justify-end gap-2 border-t border-neutral/10 px-5 pt-5">
          <Button
            variant="outline"
            color="neutral"
            className="border-neutral/20"
            onClick={resetChanges}
            isDisabled={!hasChanges}
          >
            Discard changes
          </Button>
          <Button isDisabled={!hasChanges} onClick={handleSaveChanges}>
            Save changes
          </Button>
        </div>
      </ContentCard>
    </div>
  );
};
